import React from 'react'
import { useLocation, Redirect } from 'react-router'

// Constants
import { routes } from 'constants/routes'

// Components
import LoadingState from 'modules/shares/components/LoadingState'

import ProjectShares, {
  ProjectSharesProps,
} from '../../components/ProjectShares'
import { useGetProjects } from '../../operations'

interface ProjectsViewProps extends OmitSafe<ProjectSharesProps, 'projectId'> {
  workspaceId: string
  debouncedSearch: string
  projectId?: string
  userIsEditor: boolean
  userCanRenameProject: boolean
}

/**
 * ProjectsView
 *
 * This view tries to determine if the project is a draft project or a normal project.
 * and renders the ProjectShares for the appropriate project.
 */
const ProjectsView = ({
  workspaceId,
  projectId,
  debouncedSearch,
  userIsEditor,
  userCanRenameProject,
}: ProjectsViewProps) => {
  const location = useLocation()

  const { draftsProject, loading } = useGetProjects({ workspaceId })

  if (loading) {
    return <LoadingState />
  }

  const draftProjectId = draftsProject?.entries?.[0]?.shortId
  const draftsRoute = routes.WORKSPACE_DRAFTS.create({ workspaceId })

  const isDraftsRoute = location.pathname === draftsRoute
  const isDraftRouteUsingProjectId =
    draftsProject && draftProjectId === projectId

  if (isDraftRouteUsingProjectId) {
    // The draft project can also be accessed using the URL format of a normal project.
    // This is accepted but we need to redirect to the proper draft route for consistency
    //  but also so that the "Drafts" menu NavLink can set the active state.
    // i.e.: If project id "HELLO" is a draft project: workspace/ID/p/HELLO -> workspace/ID/drafts
    return <Redirect to={routes.WORKSPACE_DRAFTS.create({ workspaceId })} />
  }

  return (
    <ProjectShares
      search={debouncedSearch}
      workspaceId={workspaceId}
      projectId={isDraftsRoute ? draftProjectId! : projectId!}
      userIsEditor={userIsEditor}
      userCanRenameProject={userCanRenameProject}
    />
  )
}

export default ProjectsView
